import { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Bell } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import api from '@/lib/api';
import { NotificationModal } from "./NotificationModal";
import { BACKEND_URL } from "../config";
// Notification sound using Web Audio API
const playNotificationSound = () => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    
    // Create a pleasant notification sound
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    // Bell-like sound: two quick tones
    oscillator.frequency.setValueAtTime(880, audioContext.currentTime); // A5
    oscillator.frequency.setValueAtTime(1100, audioContext.currentTime + 0.1); // C#6
    
    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
    
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.3);
  } catch (error) {
    // Audio might be blocked, silently fail
  }
};

export function NotificationBell() {
  const { t } = useTranslation("notifications");
  const [unreadCount, setUnreadCount] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pollingMode, setPollingMode] = useState("interval"); // "page_load" or "interval"
  const [pollingInterval, setPollingInterval] = useState(30000); // 30 seconds default
  const [isAnimating, setIsAnimating] = useState(false);
  const intervalRef = useRef(null);
  const prevCountRef = useRef(0);
  const isFirstLoad = useRef(true);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await api.get("/api/notifications/unread-count");
      const newCount = res.data.unread_count || 0;
      
      // Only trigger animation if count increased (not on first load)
      if (!isFirstLoad.current && newCount > prevCountRef.current && prevCountRef.current >= 0) {
        // Play sound
        playNotificationSound();
        
        // Trigger bell animation
        setIsAnimating(true);
        setTimeout(() => setIsAnimating(false), 1000);
        
        // Dispatch event to trigger toast fetch
        window.dispatchEvent(new CustomEvent('newNotificationsAvailable', {
          detail: { newCount: newCount - prevCountRef.current }
        }));
      }
      
      prevCountRef.current = newCount;
      isFirstLoad.current = false;
      setUnreadCount(newCount);
    } catch (error) {
      // Silently fail - user might not be authenticated
    }
  }, []);

  const fetchSettings = useCallback(async () => {
    try {
      const res = await api.get("/api/notifications/settings");
      const mode = res.data.polling_mode || "interval";
      const intervalSeconds = res.data.polling_interval_seconds || 30;
      setPollingMode(mode);
      setPollingInterval(intervalSeconds * 1000);
    } catch (error) {
      // Use default
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    const init = async () => {
      await fetchSettings();
      await fetchUnreadCount();
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listen for custom event to open modal (from NotificationLoginPopup)
  useEffect(() => {
    const handleOpenNotifications = () => {
      setIsModalOpen(true);
    };
    
    window.addEventListener('openNotificationsModal', handleOpenNotifications);
    return () => {
      window.removeEventListener('openNotificationsModal', handleOpenNotifications);
    };
  }, []);

  // Listen for notification read events from toast container
  useEffect(() => {
    const handleNotificationRead = () => {
      // Refresh count when a notification is marked as read via toast
      fetchUnreadCount();
    };
    
    window.addEventListener('notificationRead', handleNotificationRead);
    return () => {
      window.removeEventListener('notificationRead', handleNotificationRead);
    };
  }, [fetchUnreadCount]);

  // Polling - only if mode is "interval"
  useEffect(() => {
    // Clear any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    // Only set up polling if mode is "interval"
    if (pollingMode === "interval") {
      intervalRef.current = setInterval(fetchUnreadCount, pollingInterval);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [pollingMode, pollingInterval, fetchUnreadCount]);

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    // Refresh count after closing modal
    fetchUnreadCount();
  };

  const getTooltipText = () => {
    if (unreadCount === 0) {
      return t("bell_tooltip");
    }
    return unreadCount === 1 
      ? t("bell_tooltip_unread", { count: unreadCount })
      : t("bell_tooltip_unread_plural", { count: unreadCount });
  };

  return (
    <>
      <TooltipProvider delayDuration={100}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="relative"
              onClick={handleOpenModal}
              data-testid="notification-bell"
            >
              <Bell 
                className={`h-5 w-5 ${unreadCount > 0 ? 'text-primary' : 'text-muted-foreground'} ${isAnimating ? 'animate-bounce' : ''}`} 
              />
              {unreadCount > 0 && (
                <span 
                  className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white animate-pulse"
                  data-testid="notification-badge"
                >
                  {unreadCount > 99 ? "99+" : unreadCount}
                </span>
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="z-[10000]">
            {getTooltipText()}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <NotificationModal 
        isOpen={isModalOpen} 
        onClose={handleCloseModal}
        onUnreadCountChange={setUnreadCount}
      />
    </>
  );
}

export default NotificationBell;
